import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Upload, X, Image as ImageIcon } from "lucide-react";
import { sb } from "@/lib/sb";
import { uploadMediaItem } from "@/lib/media-library.functions";
import { encodeForUpload } from "@/lib/image-encode";

export const Route = createFileRoute("/_authenticated/dashboard/projects")({
  component: ProjectsPage,
});


type VariantMap = Record<string, string>;

type Project = {
  id: string;
  name: string;
  photos: string[];
  primary_photo_index: number;
  category: string;
  description: string;
  location: string;
  date_completed: string | null;
  is_featured: boolean;
  is_published: boolean;
  sort_order: number;
  before_photo: string | null;
  after_photo: string | null;
  before_label: string | null;
  after_label: string | null;
  before_after_enabled: boolean;

  photo_variants: Record<string, VariantMap> | null;
};

const empty: Omit<Project, "id"> = {
  name: "",
  photos: [],
  primary_photo_index: 0,
  category: "",
  description: "",
  location: "",
  date_completed: null,
  is_featured: false,
  is_published: true,
  sort_order: 0,
  before_photo: null,
  after_photo: null,
  before_label: null,
  after_label: null,
  before_after_enabled: false,

  photo_variants: null,
};



function ProjectsPage() {
  const [items, setItems] = useState<Project[]>([]);
  const [editing, setEditing] = useState<Project | null>(null);
  const [adding, setAdding] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);

  const load = async () => {
    const [{ data }, { data: svc }] = await Promise.all([
      sb.from("projects").select("*").order("sort_order").order("created_at", { ascending: false }),
      sb.from("service_settings").select("title").order("sort_order"),
    ]);
    setItems((data as Project[]) ?? []);
    setCategories(((svc as { title: string }[]) ?? []).map((s) => s.title));
  };
  useEffect(() => {
    load();
  }, []);

  const del = async (id: string) => {
    if (!confirm("Delete this project?")) return;
    await sb.from("projects").delete().eq("id", id);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold text-ink">Projects</h1>
        <button
          onClick={() => setAdding(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-brand hover:bg-brand-hover text-primary-foreground font-semibold text-sm"
        >
          <Plus size={16} /> Add Project
        </button>
      </div>

      <div className="space-y-2">
        {items.map((p) => (
          <div
            key={p.id}
            className="bg-card border border-border rounded-xl p-3 flex items-center gap-3"
          >
            <div className="w-14 h-14 rounded-lg bg-background border border-border overflow-hidden flex-shrink-0">
              {p.photos[p.primary_photo_index || 0] && (
                <img
                  src={p.photos[p.primary_photo_index || 0]}
                  className="w-full h-full object-cover"
                  alt=""
                />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{p.name}</p>
              <p className="text-xs text-muted-foreground">
                {p.category || "—"} · {p.location || "—"}
              </p>
            </div>
            <span
              className={`text-xs px-2 py-0.5 rounded ${
                p.is_published
                  ? "bg-emerald-100 text-emerald-700"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {p.is_published ? "Published" : "Draft"}
            </span>
            <button
              onClick={() => setEditing(p)}
              className="p-2 text-muted-foreground hover:text-brand"
            >
              <Edit2 size={14} />
            </button>
            <button
              onClick={() => del(p.id)}
              className="p-2 text-muted-foreground hover:text-destructive"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        {items.length === 0 && (
          <p className="text-center text-muted-foreground py-8 text-sm">No projects yet.</p>
        )}
      </div>

      {(editing || adding) && (
        <ProjectModal
          categories={categories}
          project={editing ?? undefined}
          onClose={() => {
            setEditing(null);
            setAdding(false);
          }}
          onSaved={() => {
            setEditing(null);
            setAdding(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function ProjectModal({
  project,
  categories,
  onClose,
  onSaved,
}: {
  project?: Project;
  categories: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [f, setF] = useState<Omit<Project, "id">>(() =>
    project ? project : { ...empty, category: categories[0] ?? "" },
  );
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pickerFor, setPickerFor] = useState<null | "photos" | "before" | "after">(null);
  const uploadMedia = useServerFn(uploadMediaItem);

  const uploadFiles = async (
    files: FileList | File[],
  ): Promise<{ url: string; variants: VariantMap | null }[]> => {
    const arr = Array.from(files).filter((x) => x.type.startsWith("image/"));
    if (arr.length === 0) return [];
    const out: { url: string; variants: VariantMap | null }[] = [];
    for (const file of arr) {
      try {
        const enc = await encodeForUpload(file);
        const row = await uploadMedia({
          data: {
            filename: file.name,
            originalMime: enc.original.mime,
            originalBase64: enc.original.base64,
            webpBase64: enc.webp.base64,
            width: enc.width,
            height: enc.height,
            sizeBytes: file.size,
            variants: enc.variants.map((v) => ({ width: v.width, base64: v.base64 })),
          },
        });
        const url = ((row as any).webp_url ?? (row as any).original_url) as string;
        const variants = ((row as any).variants ?? null) as VariantMap | null;
        out.push({ url, variants });
      } catch (e: any) {
        toast.error(`${file.name}: ${e?.message || "upload failed"}`);
      }
    }
    return out;
  };


  const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    const uploaded = await uploadFiles(files);
    setF((prev) => {
      const nextPhotos = [...prev.photos, ...uploaded.map((u) => u.url)];
      const nextVariants: Record<string, VariantMap> = { ...(prev.photo_variants || {}) };
      for (const u of uploaded) if (u.variants) nextVariants[u.url] = u.variants;
      return { ...prev, photos: nextPhotos, photo_variants: nextVariants };
    });
    setUploading(false);
    e.target.value = "";
  };



  const save = async () => {
    if (!f.name.trim()) {
      toast.error("Name required");
      return;
    }
    setSaving(true);
    const payload = { ...f, date_completed: f.date_completed || null };
    const { error } = project
      ? await sb.from("projects").update(payload).eq("id", project.id)
      : await sb.from("projects").insert(payload);
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Saved");
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-start justify-center overflow-auto p-4">
      <div className="bg-card border border-border rounded-2xl p-6 max-w-2xl w-full space-y-4 my-8">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-bold text-ink">
            {project ? "Edit Project" : "New Project"}
          </h2>
          <button onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <Field label="Name" value={f.name} onChange={(v) => setF({ ...f, name: v })} />

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground">Category (service)</label>
            <select
              value={f.category}
              onChange={(e) => setF({ ...f, category: e.target.value })}
              className="w-full mt-1 px-3 py-2 rounded-lg bg-background border border-border"
            >
              {categories.length === 0 && <option value="">— Add services first —</option>}
              {!categories.includes(f.category) && f.category && (
                <option value={f.category}>{f.category}</option>
              )}
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <Field label="Location" value={f.location} onChange={(v) => setF({ ...f, location: v })} />
        </div>

        <div>
          <label className="text-xs text-muted-foreground">Description</label>
          <textarea
            rows={3}
            value={f.description}
            onChange={(e) => setF({ ...f, description: e.target.value })}
            className="w-full mt-1 px-3 py-2 rounded-lg bg-background border border-border resize-none"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground">Date completed</label>
            <input
              type="date"
              value={f.date_completed ?? ""}
              onChange={(e) => setF({ ...f, date_completed: e.target.value || null })}
              className="w-full mt-1 px-3 py-2 rounded-lg bg-background border border-border"
            />
          </div>
          <div className="flex flex-col gap-2 pt-1">
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                className="mt-1"
                checked={f.is_featured}
                onChange={(e) => setF({ ...f, is_featured: e.target.checked })}
              />
              <span>
                <span className="font-medium">Featured</span>
                <span className="block text-[11px] text-muted-foreground leading-tight">
                  Highlighted on the homepage gallery.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                className="mt-1"
                checked={f.is_published}
                onChange={(e) => setF({ ...f, is_published: e.target.checked })}
              />
              <span>
                <span className="font-medium">Published</span>
                <span className="block text-[11px] text-muted-foreground leading-tight">
                  Visible on the public website.
                </span>
              </span>
            </label>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label className="text-xs text-muted-foreground">Photos</label>
            <button
              type="button"
              onClick={() => setPickerFor("photos")}
              className="text-xs flex items-center gap-1 text-brand hover:underline"
            >
              <ImageIcon size={12} /> Pick from library
            </button>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-1">
            {f.photos.map((url, i) => (
              <div
                key={i}
                className={`relative rounded border-2 overflow-hidden aspect-square ${
                  f.primary_photo_index === i ? "border-brand" : "border-border"
                }`}
              >
                <img src={url} className="w-full h-full object-cover" alt="" />
                <button
                  onClick={() => setF({ ...f, primary_photo_index: i })}
                  className="absolute top-1 left-1 bg-background/80 px-1 rounded text-[10px]"
                >
                  {f.primary_photo_index === i ? "Main" : "Set main"}
                </button>
                <button
                  onClick={() =>
                    setF({
                      ...f,
                      photos: f.photos.filter((_, j) => j !== i),
                      primary_photo_index: 0,
                    })
                  }
                  className="absolute top-1 right-1 bg-background/80 p-1 rounded"
                >
                  <X size={10} />
                </button>
              </div>
            ))}
            <label
              className={`flex flex-col items-center justify-center aspect-square rounded border border-dashed border-border cursor-pointer hover:border-brand text-muted-foreground ${
                uploading ? "opacity-50" : ""
              }`}
            >
              <Upload size={16} />
              <span className="text-xs mt-1">{uploading ? "…" : "Upload"}</span>
              <input
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                onChange={onUpload}
                disabled={uploading}
              />
            </label>
          </div>
        </div>


        <div className="border border-border rounded-lg p-3 space-y-3">
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              className="mt-1"
              checked={f.before_after_enabled}
              onChange={(e) => setF({ ...f, before_after_enabled: e.target.checked })}
            />
            <span>
              <span className="font-medium">Enable before / after slider</span>
              <span className="block text-[11px] text-muted-foreground leading-tight">
                Replaces the card image on the homepage with a draggable before/after comparison.
              </span>
            </span>
          </label>
          {f.before_after_enabled && (
            <div className="grid grid-cols-2 gap-3">
              <BAUpload
                label="Before photo"
                url={f.before_photo}
                onChange={(url, variants) =>
                  setF((prev) => {
                    const next = { ...prev, before_photo: url };
                    if (url && variants) {
                      next.photo_variants = { ...(prev.photo_variants || {}), [url]: variants };
                    }
                    return next;
                  })
                }
                onPickLibrary={() => setPickerFor("before")}
              />
              <BAUpload
                label="After photo"
                url={f.after_photo}
                onChange={(url, variants) =>
                  setF((prev) => {
                    const next = { ...prev, after_photo: url };
                    if (url && variants) {
                      next.photo_variants = { ...(prev.photo_variants || {}), [url]: variants };
                    }
                    return next;
                  })
                }
                onPickLibrary={() => setPickerFor("after")}
              />

              <div>
                <label className="text-xs text-muted-foreground">Before label (optional)</label>
                <input
                  type="text"
                  value={f.before_label ?? ""}
                  onChange={(e) => setF({ ...f, before_label: e.target.value || null })}
                  className="w-full mt-1 px-3 py-2 rounded-lg bg-background border border-border"
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">After label (optional)</label>
                <input
                  type="text"
                  value={f.after_label ?? ""}
                  onChange={(e) => setF({ ...f, after_label: e.target.value || null })}
                  className="w-full mt-1 px-3 py-2 rounded-lg bg-background border border-border"
                />
              </div>
              <p className="col-span-2 text-[11px] text-muted-foreground leading-tight">
                Leave blank for Before / After. Example: In Progress / Complete.
              </p>
            </div>

          )}
        </div>

        {pickerFor && (
          <MediaPicker
            multi={pickerFor === "photos"}
            onClose={() => setPickerFor(null)}
            onPick={(picked) => {
              if (pickerFor === "photos") {
                setF((prev) => {
                  const nextVariants: Record<string, VariantMap> = { ...(prev.photo_variants || {}) };
                  for (const p of picked) if (p.variants) nextVariants[p.url] = p.variants;
                  return {
                    ...prev,
                    photos: [...prev.photos, ...picked.map((p) => p.url)],
                    photo_variants: nextVariants,
                  };
                });
              } else if (pickerFor === "before") {
                setF((prev) => {
                  const p0 = picked[0];
                  const nextVariants: Record<string, VariantMap> = { ...(prev.photo_variants || {}) };
                  if (p0?.variants) nextVariants[p0.url] = p0.variants;
                  return { ...prev, before_photo: p0?.url ?? null, photo_variants: nextVariants };
                });
              } else if (pickerFor === "after") {
                setF((prev) => {
                  const p0 = picked[0];
                  const nextVariants: Record<string, VariantMap> = { ...(prev.photo_variants || {}) };
                  if (p0?.variants) nextVariants[p0.url] = p0.variants;
                  return { ...prev, after_photo: p0?.url ?? null, photo_variants: nextVariants };
                });
              }
              setPickerFor(null);
            }}
          />
        )}





        <div className="flex gap-2 pt-2">
          <button
            onClick={save}
            disabled={saving || uploading}
            className="flex-1 py-2 rounded-lg bg-brand hover:bg-brand-hover text-primary-foreground font-semibold disabled:opacity-50"
          >
            {saving ? "Saving…" : uploading ? "Wait for uploads…" : "Save"}
          </button>
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-border">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="text-xs text-muted-foreground">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 px-3 py-2 rounded-lg bg-background border border-border"
      />
    </div>
  );
}

function BAUpload({
  label,
  url,
  onChange,
  onPickLibrary,
}: {
  label: string;
  url: string | null;
  onChange: (url: string | null, variants?: VariantMap | null) => void;
  onPickLibrary: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const uploadMedia = useServerFn(uploadMediaItem);
  const upload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const enc = await encodeForUpload(file);
      const row = await uploadMedia({
        data: {
          filename: file.name,
          originalMime: enc.original.mime,
          originalBase64: enc.original.base64,
          webpBase64: enc.webp.base64,
          width: enc.width,
          height: enc.height,
          sizeBytes: file.size,
          variants: enc.variants.map((v) => ({ width: v.width, base64: v.base64 })),
        },
      });
      const newUrl = ((row as any).webp_url ?? (row as any).original_url) as string;
      const variants = ((row as any).variants ?? null) as VariantMap | null;
      onChange(newUrl, variants);
    } catch (err: any) {
      toast.error(err?.message || "Upload failed");
    }
    setBusy(false);
    e.target.value = "";
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <label className="text-xs text-muted-foreground">{label}</label>
        <button
          type="button"
          onClick={onPickLibrary}
          className="text-[11px] flex items-center gap-1 text-brand hover:underline"
        >
          <ImageIcon size={10} /> Library
        </button>
      </div>
      <div className="mt-1 relative aspect-square rounded border border-dashed border-border overflow-hidden bg-background">
        {url ? (
          <>
            <img src={url} className="w-full h-full object-cover" alt="" />
            <button
              onClick={() => onChange(null)}
              className="absolute top-1 right-1 bg-background/80 p-1 rounded"
              type="button"
            >
              <X size={10} />
            </button>
          </>
        ) : (
          <label className="flex flex-col items-center justify-center h-full cursor-pointer text-muted-foreground hover:text-brand">
            <Upload size={16} />
            <span className="text-xs mt-1">{busy ? "…" : "Upload"}</span>
            <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={busy} />
          </label>
        )}
      </div>
    </div>
  );
}

type LibraryItem = {
  id: string;
  filename: string;
  webp_url: string | null;
  original_url: string | null;
  variants: VariantMap | null;
  created_at: string;
};


function MediaPicker({
  multi,
  onClose,
  onPick,
}: {
  multi: boolean;
  onClose: () => void;
  onPick: (picked: { url: string; variants: VariantMap | null }[]) => void;
}) {
  const [items, setItems] = useState<LibraryItem[]>([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const { data } = await sb
        .from("media_library")
        .select("id, filename, webp_url, original_url, variants, created_at")
        .order("created_at", { ascending: false });
      setItems((data as unknown as LibraryItem[]) ?? []);
      setLoading(false);
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const visible = items.filter((i) => !/-crop\.[a-z0-9]+$/i.test(i.filename));
    if (!q) return visible;
    return visible.filter((i) => i.filename.toLowerCase().includes(q));
  }, [items, query]);

  const toggle = (id: string) => {
    if (!multi) {
      const it = items.find((x) => x.id === id);
      const url = it?.webp_url ?? it?.original_url;
      if (url) onPick([{ url, variants: it?.variants ?? null }]);
      return;
    }
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const confirm = () => {
    const picked = filtered
      .filter((i) => selected.has(i.id))
      .map((i) => {
        const url = (i.webp_url ?? i.original_url) as string | null;
        return url ? { url, variants: i.variants ?? null } : null;
      })
      .filter(Boolean) as { url: string; variants: VariantMap | null }[];
    onPick(picked);
  };


  return (
    <div className="fixed inset-0 z-[60] bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl p-4 w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg font-bold text-ink">Media library</h3>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search filename…"
          className="w-full px-3 py-2 rounded-lg bg-background border border-border mb-3 text-sm"
        />
        <div className="flex-1 overflow-auto">
          {loading ? (
            <p className="text-center text-muted-foreground py-8 text-sm">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="text-center text-muted-foreground py-8 text-sm">No media found.</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
              {filtered.map((it) => {
                const url = it.webp_url ?? it.original_url ?? "";
                const isSel = selected.has(it.id);
                return (
                  <button
                    type="button"
                    key={it.id}
                    onClick={() => toggle(it.id)}
                    className={`relative aspect-square rounded overflow-hidden border-2 ${
                      isSel ? "border-brand" : "border-border"
                    }`}
                  >
                    <img src={url} alt={it.filename} className="w-full h-full object-cover" />
                    {isSel && (
                      <span className="absolute inset-0 bg-brand/20 flex items-center justify-center text-brand font-bold">
                        ✓
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        {multi && (
          <div className="flex gap-2 pt-3">
            <button
              onClick={confirm}
              disabled={selected.size === 0}
              className="flex-1 py-2 rounded-lg bg-brand hover:bg-brand-hover text-primary-foreground font-semibold disabled:opacity-50"
            >
              Add {selected.size || ""} photo{selected.size === 1 ? "" : "s"}
            </button>
            <button onClick={onClose} className="px-4 py-2 rounded-lg border border-border">Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}


